<template>
  <section
    class="RunInviteButtonView"
  >
    <FormButton
      v-if="isCommish"
      iconName="peoplePlus"
      orientation="vertical"
      @click="isMenuVisible = true"
    >
      Invite players
    </FormButton>

    <FormButton
      v-else
      data-testid="runInviteButton"
      iconName="personWithArmsRaised"
      @click="openModal({name: RouteName.runInvite, params:{runId: run.id}})"
    >
      {{ isCommish ? "Invite a guest to this run" : "Invite a guest player" }}
    </FormButton>

    <Teleport to="body">
      <Transition>
        <MenuModal
          v-if="isMenuVisible"
          data-testid="runInviteMenuModal"
          @close="isMenuVisible = false"
        >
          <MenuModalButton
            v-for="item in menuItems"
            :key="item.id"
            v-bind="item"
            @click="handleMenuItemClick(item)"
          />
        </MenuModal>
      </Transition>

      <Transition>
        <RunInviteModalView
          v-if="isModalOpen"
          v-bind="{run}"
          @close="closeActiveModal()"
        />
      </Transition>
    </Teleport>
  </section>
</template>

<script lang="ts" setup>
import FormButton from "@/components/FormButton.vue";
import { ModalMenuItem } from "@/components/MenuModal.types.ts";
import MenuModal from "@/components/MenuModal.vue";
import MenuModalButton from "@/components/MenuModalButton.vue";
import useModalRoute from "@/composables/useModalRoute";
import { RouteName } from "@/lib/router/routes";
import haptic from "@/lib/utils/platform/haptic.ts";
import getRouteForRun from "@/lib/utils/route/getRouteForRun.ts";
import useModalStore from "@/stores/useModalStore";
import { RunWithMaybeCourt } from "@shared/lib/database/query.types";
import delay from "@shared/lib/utils/async/delay.ts";
import { computed, ref } from "vue";
import { useRouter } from "vue-router";
import RunInviteModalView from "./modals/RunInviteModalView.vue";

const {
  run,
} = defineProps<{
  run: RunWithMaybeCourt,
  isCommish?: boolean,
}>();

const router = useRouter();

const {
  openModal,
  closeActiveModal,
} = useModalStore();

// TODO: extract modal into root
const {
  isModalOpen,
} = useModalRoute(RouteName.runInvite);

const isMenuVisible = ref(false);
const isUrlCopied = ref(false);

const menuItems = computed((): Array<ModalMenuItem> => {
  return [
    {
      id: "link",
      iconName: "link",
      title: isUrlCopied.value ? "Copied!" : "Copy link",
      subtitle: "Only crew members can view the run from this link",
    },
    {
      id: "guest",
      iconName: "personWithArmsRaised",
      title: "Invite a guest to this run",
      subtitle: "Guests can only view this run and join the waitlist",
    },
    {
      id: "player",
      iconName: "peoplePlus",
      title: "Invite a player to your crew",
      subtitle: "Crew members can view all runs and update their status",
    },
  ];
});

function handleMenuItemClick (item: ModalMenuItem) {
  switch (item.id) {
    case "link":
      handleCopyLink();
      break;
    case "guest":
      isMenuVisible.value = false;
      openModal({
        name: RouteName.runInvite,
        params:{ runId: run.id },
      });
      break;
    case "player":
      isMenuVisible.value = false;
      openModal({ name: RouteName.invitePlayer });
      break;
  }
}

async function handleCopyLink () {
  const url = new URL(router.resolve(getRouteForRun(run.id)).fullPath, "https://ball.app"); // TODO: use share url helper

  haptic();
  await navigator.clipboard.writeText(url.toString());
  isUrlCopied.value = true;
  await delay(1000);
  isUrlCopied.value = false;
}
</script>
